import { Component, Prop, Vue } from 'vue-property-decorator';
import MiForm from '@/components/MiForm/MiForm.vue';
import { MiFormField } from '@/components/MiForm/MiForm';
import MiStepDialog from '@/components/MiStepDialog/MiStepDialog.vue';
import { Address } from '@/models/Address';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import { User, CreateUserPayload } from '@/models/User';

@Component<CreateUserDialog>({ })
export default class CreateUserDialog extends Vue {
  @Prop()
  protected contractorId!: string;

  protected isLoading = false;

  protected isSuccessful = false;

  protected isFailed = false;

  protected payload: CreateUserPayload = {};

  protected currentStep = 1;

  protected activeTab: number | null = 1;

  protected isFindingAddress = false;

  protected addressError: string | null = null;

  protected address: Address | null = null;

  protected autoFillAddress = true;

  protected passwordErrorMessage = '';

  protected onStepChanged(step: number): void {
    this.currentStep = step;
    this.validateCurrentForm();
  }

  protected async validateCurrentForm(): Promise<void> {
    switch (this.currentStep) {
      default:
      case 1: this.setFormValid(this.isUserFormValid()); break;
      case 2: this.setFormValid(await this.isPasswordFormValid()); break;
    }
  }

  protected close(): void {
    this.$emit('input', false);
  }

  protected async onClickCreate(): Promise<User | void> {
    this.isLoading = true;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = this.payload;

    const response: User | null = await new User()
      .create(payload)
      .catch((error: AxiosError): null => {
        ErrorHandler.network(error);
        this.isFailed = true;
        return null;
      });

    this.isLoading = false;

    if (! response) {
      this.isFailed = true;
      return;
    }

    this.isSuccessful = true;
    this.$emit('isCreated');
    setTimeout(() => {
      this.close();
    }, 2000);
  }

  protected async handleFieldChanged(): Promise<void> {
    this.payload = await this.createPayload();
    this.validateCurrentForm();
  }

  protected isUserFormValid(): boolean {
    if (! this.userForm) { return false; }
    const isValid: boolean = this.userForm.isFormValid();
    if (! isValid) { return false; }
    return true;
  }

  protected async isPasswordFormValid(): Promise<boolean> {
    if (! this.passwordForm) { return false; }
    const data = await this.passwordForm.parseFromData();

    if (data.password && data.confirm_password && (data.password !== data.confirm_password)) {
      this.passwordErrorMessage = 'Wachtwoorden komen niet overeen';
      return false;
    }

    this.passwordErrorMessage = '';
    return data.password === data.confirm_password;
  }

  protected setFormValid(valid: boolean): void {
    this.stepDialog.setValid(valid);
  }

  protected async createPayload(): Promise<CreateUserPayload> {
    const passwordForm = await this.passwordForm.parseFromData();
    const payload: CreateUserPayload = {
      ...await this.userForm.parseFromData(),
      password: passwordForm.password,
    };

    if (this.contractorId) {
      payload.hin_type = 'contractor';
      payload.contractor = this.contractorId;
    }

    return payload;
  }

  protected get userForm(): MiForm {
    return this.$refs.userForm as MiForm;
  }

  protected get passwordForm(): MiForm {
    return this.$refs.passwordForm as MiForm;
  }

  protected get stepDialog(): MiStepDialog {
    return this.$refs.stepDialog as MiStepDialog;
  }

  protected get userFields(): MiFormField[] {
    return [
      {
        type: 'text',
        key: 'first_name',
        placeholder: 'Voornaam',
        label: 'Voornaam',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'last_name',
        placeholder: 'Achternaam',
        label: 'Achternaam',
        rules: ['required'],
      },
      {
        type: 'text',
        key: 'phone',
        placeholder: 'Telefoonnummer',
        label: 'Telefoonnummer',
        rules: ['required'],
      },
      {
        type: 'email',
        key: 'email',
        placeholder: 'E-mailadres',
        label: 'E-mailadres',
      },
    ];
  }

  protected get passwordFields(): MiFormField[] {
    return [
      {
        type: 'password',
        key: 'password',
        placeholder: 'Wachtwoord',
        label: 'Wachtwoord',
        rules: ['required', 'password'],
      },
      {
        type: 'password',
        key: 'confirm_password',
        placeholder: 'Wachtwoord bevestigen',
        label: 'Wachtwoord bevestigen',
        rules: ['required'],
      },
    ];
  }
}
